// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@baretta.com).

import { z } from 'zod';
import {
    buildStoredJsonValueParser,
    defineStoredJsonContract,
} from '../../../stored-json-contract.types';

export interface CloverOAuthTokenSetV1 {
    readonly clover_oauth_token_set_version: 1;
    readonly access_token: string;
    readonly refresh_token: string;
    readonly recovery_token?: string;
}

export type CloverOAuthTokenSet = CloverOAuthTokenSetV1;

export const CloverOAuthTokenSetV1Schema: z.ZodType<CloverOAuthTokenSetV1> = z
    .object({
        clover_oauth_token_set_version: z.literal(1),
        access_token: z.string().min(1),
        refresh_token: z.string().min(1),
        recovery_token: z.string().min(1).optional(),
    })
    .strict();

export const CloverOAuthTokenSetStorageContract = defineStoredJsonContract({
    contractName: 'CloverOAuthTokenSet',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'CloverOAuthTokenSetV1Schema',
            schema: CloverOAuthTokenSetV1Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['clover_oauth_token_set_version'],
                    markerValue: 1,
                },
            ],
            normalize: (value: CloverOAuthTokenSetV1): CloverOAuthTokenSet => value,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v1',
});

export const StoredCloverOAuthTokenSetParser: z.ZodType<CloverOAuthTokenSet> =
    buildStoredJsonValueParser(CloverOAuthTokenSetStorageContract);

export function serializeCloverOAuthTokenSetV1(
    value: CloverOAuthTokenSet
): CloverOAuthTokenSetV1 {
    return CloverOAuthTokenSetV1Schema.parse(value);
}

export interface CloverOAuthCallbackMaterialV1 {
    readonly clover_oauth_callback_material_version: 1;
    readonly pkce_verifier?: string;
}

export interface CloverOAuthCallbackMaterialV2 {
    readonly clover_oauth_callback_material_version: 2;
    readonly expected_merchant_id: string;
    readonly pkce_verifier?: string;
}

export interface CloverOAuthCallbackMaterial {
    readonly expected_merchant_id?: string;
    readonly pkce_verifier?: string;
}

export const CloverOAuthCallbackMaterialV1Schema: z.ZodType<CloverOAuthCallbackMaterialV1> =
    z
        .object({
            clover_oauth_callback_material_version: z.literal(1),
            pkce_verifier: z.string().min(43).max(128).optional(),
        })
        .strict();

export const CloverOAuthCallbackMaterialV2Schema: z.ZodType<CloverOAuthCallbackMaterialV2> =
    z
        .object({
            clover_oauth_callback_material_version: z.literal(2),
            expected_merchant_id: z.string().min(1),
            pkce_verifier: z.string().min(43).max(128).optional(),
        })
        .strict();

export const CloverOAuthCallbackMaterialStorageContract = defineStoredJsonContract({
    contractName: 'CloverOAuthCallbackMaterial',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'CloverOAuthCallbackMaterialV1Schema',
            schema: CloverOAuthCallbackMaterialV1Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['clover_oauth_callback_material_version'],
                    markerValue: 1,
                },
            ],
            normalize: (value: CloverOAuthCallbackMaterialV1): CloverOAuthCallbackMaterial => ({
                pkce_verifier: value.pkce_verifier,
            }),
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'CloverOAuthCallbackMaterialV2Schema',
            schema: CloverOAuthCallbackMaterialV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['clover_oauth_callback_material_version'],
                    markerValue: 2,
                },
            ],
            normalize: (value: CloverOAuthCallbackMaterialV2): CloverOAuthCallbackMaterial => ({
                expected_merchant_id: value.expected_merchant_id,
                pkce_verifier: value.pkce_verifier,
            }),
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v2',
});

export const StoredCloverOAuthCallbackMaterialParser: z.ZodType<CloverOAuthCallbackMaterial> =
    buildStoredJsonValueParser(CloverOAuthCallbackMaterialStorageContract);

export function serializeCloverOAuthCallbackMaterialV2(
    value: CloverOAuthCallbackMaterial
): CloverOAuthCallbackMaterialV2 {
    return CloverOAuthCallbackMaterialV2Schema.parse({
        clover_oauth_callback_material_version: 2,
        ...value,
    });
}
